/**
 * @fileoverview The `flags` singleton — the shortest possible integration.
 * @module lib/features/core/flagsGlobal
 *
 * ```ts
 * import { flags } from 'flowgrid-sdk';
 *
 * if (flags.on('new-checkout')) renderNewCheckout();
 * ```
 *
 * No provider, no context, no instance to thread through your app: this reads
 * from whatever `FlowGrid.init()` created. Every method is safe to call before
 * init, on the server, and with the SDK blocked by an ad blocker — you get your
 * default, never an exception.
 */

import {
  resolveFlagEngine,
  valueFrom,
  variantFrom,
  type FlagEngineLike,
} from './flagRegistry';
import type { FlagDecision, FlagVariantValue } from './flagEval';
import type { FlagOverride } from './flagOverrides';
import type { FlagLookupPolicy, FlagReading } from './flags';
import { FeatureFlags as FeatureFlagsEngine } from './flags';
import type { FeatureFlags, FlagContext } from './flags';

/**
 * Whatever is answering flags right now: an engine registered with
 * `setFlagEngine()` first, else the one `FlowGrid.init()` created.
 *
 * The type says exactly what a caller may assume. `FlagEngineLike` is the
 * contract every engine satisfies — `on`, `ready`, `onChange`, `isReady` — and
 * the rest is optional, because a bring-your-own source is not obliged to
 * implement `identify` (it has no rules to re-evaluate) or `all`. Calling those
 * optionally is a real guard, not defensive noise.
 */
type ActiveEngine = FlagEngineLike & Partial<FeatureFlags>;

function engine(): ActiveEngine | null {
  return resolveFlagEngine() as ActiveEngine | null;
}

/** How long `onChange` keeps looking for an SDK that hasn't initialised yet. */
const ATTACH_TIMEOUT_MS = 10_000;
const ATTACH_INTERVAL_MS = 100;

export const flags = {
  /**
   * Is this flag on for the current visitor?
   *
   * @param key      The flag key from your dashboard.
   * @param fallback What to return when the answer isn't known here — flag not
   *                 created, bundle not loaded, SDK not initialised, offline.
   *                 Your code's own default; pass the honest one.
   */
  on(key: string, fallback = false): boolean {
    return engine()?.on(key, fallback) ?? fallback;
  },

  /** `!on(key)`, for guard clauses that read better in the negative. */
  off(key: string, fallback = false): boolean {
    return !flags.on(key, fallback);
  },

  /**
   * Which variant is this visitor on?
   *
   * ```ts
   * if (flags.variant('checkout-copy') === 'urgent') showUrgentCTA();
   * ```
   *
   * `null` for a plain on/off flag, for a flag that is switched off, for a
   * visitor outside the rollout, and before flags load. `null` is not the
   * control arm: outside an experiment and inside its control group are
   * different facts, and treating them alike is how a result stops meaning
   * anything.
   */
  variant(key: string): string | null {
    return variantFrom(engine(), key);
  },

  /**
   * The value this visitor's variant carries — the dynamic-config read.
   *
   * ```ts
   * const { retries } = flags.get('http-config', { retries: 3 });
   * const heading     = flags.get('hero-copy', 'Get started');
   * ```
   *
   * `fallback` is your own default and covers every way there can be no value:
   * flags not loaded, flag not created, flag switched off, a plain gate, or an
   * arm with no value attached. The return type comes from the fallback.
   */
  get<T extends FlagVariantValue>(key: string, fallback: T): T {
    return valueFrom(engine(), key, fallback);
  },

  /**
   * The full answer plus why — value, arm, arm's value, and which step decided.
   * For debugging a flag that isn't doing what you expect.
   *
   * Deliberately does not count toward the usage digest: asking why is not
   * reading the flag, and a debug call in a loop should not distort the numbers
   * that decide whether a flag is safe to delete.
   */
  explain(key: string, fallback = false): FlagReading {
    const active = engine();
    // An engine from before variants has no `explain`. Reconstruct the honest
    // subset from what it does have rather than returning nothing.
    if (!active?.explain) {
      return { value: flags.on(key, fallback), variant: null, reason: 'unknown_flag', source: 'unknown' };
    }
    // A custom engine may implement `explain` and still not report a source —
    // the type says it must, but the whole point of `setFlagEngine` is that the
    // object came from someone else's codebase. `unknown` is the floor.
    const reading = active.explain(key, fallback) as Partial<FlagReading> & FlagDecision;
    return { ...reading, source: reading.source ?? 'unknown' };
  },

  /**
   * Force answers while you test, for this tab — never counted, never an
   * exposure. `null` puts a key back.
   *
   * ```ts
   * flags.override({ 'checkout-copy': 'urgent' });
   * ```
   *
   * Or open the page with `?fg_flags=checkout-copy:urgent` (local hosts, or with
   * `flags: { allowUrlOverrides: true }`). A no-op before init.
   */
  override(next: Record<string, FlagOverride | null>): void {
    engine()?.override?.(next);
  },

  /** Drop every forced answer for this tab. */
  clearOverrides(): void {
    engine()?.clearOverrides?.();
  },

  /**
   * How this client fetches and keeps answers — see `FlagLookupPolicy`.
   *
   * `null` when the active engine is not ours, because a policy read off an
   * engine that has none would be this module's guess rather than the truth.
   */
  lookupPolicy(): FlagLookupPolicy | null {
    return engine()?.lookupPolicy ?? null;
  },

  /** Every flag this client can see, evaluated. For debugging and SSR handoff. */
  all(): Record<string, boolean> {
    return engine()?.all?.() ?? {};
  },

  /**
   * Resolves once flags have loaded (or definitively failed to). Await this to
   * hold a paint instead of rendering the default and snapping to the real
   * value a moment later.
   */
  ready(): Promise<void> {
    return engine()?.ready() ?? Promise.resolve();
  },

  /** True once flags have loaded. */
  get isReady(): boolean {
    return engine()?.isReady ?? false;
  },

  /**
   * Add to what flags know about this visitor — plan, region, custom attributes.
   * `fg.identifyUser()` already does this for the user id and traits, so most
   * apps never call it.
   */
  identify(context: FlagContext): void {
    engine()?.identify?.(context);
  },

  /**
   * Re-run something whenever flag values change: the bundle arriving, a
   * server-side resolution landing, or `identify()` changing who the visitor is.
   * Returns an unsubscribe function.
   *
   * Calling this before `FlowGrid.init()` is fine — it waits for the SDK to
   * appear (briefly, then gives up rather than polling forever).
   */
  onChange(listener: () => void): () => void {
    const direct = engine();
    if (direct) return direct.onChange(listener);

    let unsubscribe: (() => void) | null = null;
    let cancelled = false;
    const deadline = Date.now() + ATTACH_TIMEOUT_MS;

    const attach = () => {
      if (cancelled) return;
      const engineNow = engine();
      if (engineNow) {
        unsubscribe = engineNow.onChange(listener);
        // The engine may already have loaded while we were waiting; tell the
        // subscriber once so it doesn't sit on a stale first render.
        listener();
        return;
      }
      if (Date.now() < deadline) setTimeout(attach, ATTACH_INTERVAL_MS);
    };
    setTimeout(attach, ATTACH_INTERVAL_MS);

    return () => {
      cancelled = true;
      unsubscribe?.();
    };
  },
};

export type Flags = typeof flags;

/**
 * Typed flag keys, without a code generator.
 *
 * ```ts
 * // flags.ts
 * import { defineFlags } from 'flowgrid-sdk';
 * export const flags = defineFlags(['new-checkout', 'dark-mode'] as const);
 *
 * // anywhere
 * import { flags } from './flags';
 * flags.on('new-checkout');   // ✅
 * flags.on('new-chekcout');   // ❌ compile error
 * ```
 *
 * The alternative — `npx flowgrid types` writing a union from the dashboard —
 * means a build step, a credential, and a generated file that goes stale on
 * every branch. Declaring the keys where you use them is one line, works
 * offline, and reviews as a diff.
 *
 * Everything else behaves exactly like the untyped singleton: synchronous,
 * never throws, unknown keys still return the fallback.
 */
export function defineFlags<const K extends readonly string[]>(keys: K) {
  type Key = K[number];
  return {
    /** The keys you declared, for iteration or debugging. */
    keys: keys as unknown as readonly Key[],
    on(key: Key, fallback = false): boolean {
      return flags.on(key, fallback);
    },
    off(key: Key, fallback = false): boolean {
      return flags.off(key, fallback);
    },
    ready(): Promise<void> {
      return flags.ready();
    },
    onChange(listener: () => void): () => void {
      return flags.onChange(listener);
    },
  };
}

/**
 * A standalone feature-flag client.
 *
 * `FlowGrid.init()` already gives you `fg.flags`, and the `flags` singleton
 * above reads from it — that is the path almost everyone should take. This
 * exists for the cases where the full SDK is the wrong shape:
 *
 *   - a framework or runtime with no analytics story (a CLI, a worker, a
 *     backend service that renders flags into a page);
 *   - a test that wants a real engine pointed at a fixture server;
 *   - an app that already has analytics and wants flags only.
 *
 * It is the same engine, with the same guarantees: `on()` is synchronous, never
 * throws, and returns your default until the bundle lands.
 *
 * ```ts
 * import { createFlags } from 'flowgrid-sdk';
 *
 * const flags = createFlags({ webId: 'â€¦', context: { environment: 'production' } });
 * await flags.ready();
 * if (flags.on('new-checkout')) { … }
 * ```
 */
export function createFlags(options: {
  /** Your site's web id — the same one you pass to `FlowGrid.init`. */
  webId: string;
  /** Override the API host. Defaults to FlowGrid's. */
  endpoint?: string;
  /**
   * What you know about the subject: user id, plan, region, attributes.
   *
   * Pass `visitorId` if you use percentage rollouts here. A standalone engine
   * has no identity manager to read one from, and without it every subject
   * falls back to `webId` — the same value for everyone, so a percentage rule
   * would be on for all of them or none. `FlowGrid.init()` fills this in for
   * you; this path cannot.
   */
  context?: FlagContext;
  /** Skip the initial fetch — you'll call `load()` yourself. */
  lazy?: boolean;
}): FeatureFlags {
  const engine = new FeatureFlagsEngine(
    options.webId,
    options.endpoint ?? 'https://core.flow-grid.xyz',
    '',
  );
  if (!options.lazy) void engine.load(options.context).catch(() => null);
  else if (options.context) engine.identify(options.context);
  return engine;
}
